import React, { useState } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Sparkles, Loader2, TrendingUp, TrendingDown, AlertTriangle,
  Wheat, Calculator, Target, DollarSign, BarChart3, Lightbulb
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";

const culturas = [
  { value: "soja", label: "Soja", custoMedio: 3500, produtividadeMedia: 55, precoMedio: 135 },
  { value: "milho", label: "Milho", custoMedio: 4200, produtividadeMedia: 120, precoMedio: 65 },
  { value: "milho_safrinha", label: "Milho Safrinha", custoMedio: 3000, produtividadeMedia: 90, precoMedio: 58 },
  { value: "algodao", label: "Algodão", custoMedio: 12000, produtividadeMedia: 280, precoMedio: 140 },
  { value: "cafe", label: "Café", custoMedio: 18000, produtividadeMedia: 35, precoMedio: 1200 },
  { value: "cana", label: "Cana-de-açúcar", custoMedio: 8000, produtividadeMedia: 80, precoMedio: 130 },
  { value: "trigo", label: "Trigo", custoMedio: 2800, produtividadeMedia: 45, precoMedio: 90 },
  { value: "feijao", label: "Feijão", custoMedio: 3200, produtividadeMedia: 25, precoMedio: 300 },
];

export default function Simulador() {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [analiseIA, setAnaliseIA] = useState(null);

  const [formData, setFormData] = useState({
    cultura: "soja",
    area: 100,
    custoHectare: 3500,
    produtividadeEsperada: 55,
    precoVenda: 135,
    variacaoInsumos: 0,
    variacaoPreco: 0,
    riscoCLimatico: 10
  });

  const { data: propriedade } = useQuery({
    queryKey: ['propriedade', propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.Propriedade.filter({ id: propriedadeId }).then(r => r[0])
      : Promise.resolve(null),
    enabled: !!propriedadeId
  });

  const { data: lancamentos = [] } = useQuery({
    queryKey: ['lancamentos', propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.Lancamento.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const handleCulturaChange = (value) => {
    const cultura = culturas.find(c => c.value === value);
    setFormData({
      ...formData,
      cultura: value,
      custoHectare: cultura.custoMedio,
      produtividadeEsperada: cultura.produtividadeMedia,
      precoVenda: cultura.precoMedio
    });
    setResultado(null);
    setAnaliseIA(null);
  };

  const calcularSimulacao = () => {
    // Ajustes baseados nas variações
    const custoAjustado = formData.custoHectare * (1 + formData.variacaoInsumos / 100);
    const precoAjustado = formData.precoVenda * (1 + formData.variacaoPreco / 100);
    const produtividadeAjustada = formData.produtividadeEsperada * (1 - formData.riscoCLimatico / 100);

    // Cenário Otimista
    const custoOtimista = formData.custoHectare * 0.9;
    const precoOtimista = formData.precoVenda * 1.15;
    const prodOtimista = formData.produtividadeEsperada * 1.1;

    // Cenário Pessimista
    const custoPessimista = formData.custoHectare * 1.2;
    const precoPessimista = formData.precoVenda * 0.85;
    const prodPessimista = formData.produtividadeEsperada * 0.7;

    const calcularCenario = (custo, preco, prod) => {
      const custoTotal = custo * formData.area;
      const producaoTotal = prod * formData.area;
      const receitaTotal = producaoTotal * preco;
      const lucro = receitaTotal - custoTotal;
      const margem = receitaTotal > 0 ? (lucro / receitaTotal) * 100 : 0;
      const breakEven = preco > 0 ? custo / preco : 0;
      
      return {
        custoTotal,
        producaoTotal,
        receitaTotal,
        lucro,
        margem,
        custoPorSaca: custo / prod,
        breakEven
      };
    };

    setResultado({
      realista: calcularCenario(custoAjustado, precoAjustado, produtividadeAjustada),
      otimista: calcularCenario(custoOtimista, precoOtimista, prodOtimista),
      pessimista: calcularCenario(custoPessimista, precoPessimista, prodPessimista)
    });
  };

  const gerarAnaliseIA = async () => {
    if (!resultado) return;
    
    setLoading(true);
    
    const prompt = `Você é um consultor agrícola experiente. Analise o seguinte cenário de produção rural e forneça recomendações práticas:

DADOS DA SIMULAÇÃO:
- Cultura: ${culturas.find(c => c.value === formData.cultura)?.label}
- Área: ${formData.area} hectares
- Custo por hectare: R$ ${formData.custoHectare}
- Produtividade esperada: ${formData.produtividadeEsperada} sacas/ha
- Preço de venda esperado: R$ ${formData.precoVenda}/saca
- Variação de insumos considerada: ${formData.variacaoInsumos}%
- Risco climático considerado: ${formData.riscoCLimatico}%

RESULTADOS CALCULADOS:
Cenário Realista:
- Custo total: R$ ${resultado.realista.custoTotal.toFixed(2)}
- Receita projetada: R$ ${resultado.realista.receitaTotal.toFixed(2)}
- Lucro esperado: R$ ${resultado.realista.lucro.toFixed(2)}
- Margem: ${resultado.realista.margem.toFixed(1)}%

Cenário Pessimista:
- Lucro: R$ ${resultado.pessimista.lucro.toFixed(2)}

Cenário Otimista:
- Lucro: R$ ${resultado.otimista.lucro.toFixed(2)}

Forneça:
1. Uma análise geral da viabilidade (máx 2 frases)
2. 3 principais riscos a monitorar
3. 3 recomendações práticas para maximizar o resultado
4. Ponto de atenção sobre o break-even

Seja direto, prático e use linguagem simples para produtores rurais.`;

    const response = await agrogestao.integrations.Core.InvokeLLM({
      prompt,
      response_json_schema: {
        type: "object",
        properties: {
          analise_geral: { type: "string" },
          riscos: { 
            type: "array", 
            items: { type: "string" }
          },
          recomendacoes: { 
            type: "array", 
            items: { type: "string" }
          },
          ponto_atencao: { type: "string" },
          classificacao_risco: {
            type: "string",
            enum: ["baixo", "medio", "alto"]
          }
        }
      }
    });

    setAnaliseIA(response);
    setLoading(false);
  };

  if (!propriedadeId) {
    return (
      <div className="text-center py-20">
        <p className="text-stone-600">Selecione uma propriedade</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-stone-800 flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-[#D4A373]" />
          Simulador de Cenários
        </h1>
        <p className="text-stone-600 mt-1">Projete lucros e analise riscos antes de plantar</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulário */}
        <div className="lg:col-span-1 space-y-6">
          <Card className="bg-white border-stone-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-purple-600" />
                Parâmetros da Simulação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label>Cultura</Label>
                <Select value={formData.cultura} onValueChange={handleCulturaChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {culturas.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Área a plantar (ha)</Label>
                <Input
                  type="number"
                  value={formData.area}
                  onChange={(e) => setFormData({...formData, area: parseFloat(e.target.value) || 0})}
                />
              </div>

              <div className="space-y-2">
                <Label>Custo por hectare (R$)</Label>
                <Input
                  type="number"
                  value={formData.custoHectare}
                  onChange={(e) => setFormData({...formData, custoHectare: parseFloat(e.target.value) || 0})}
                />
              </div>

              <div className="space-y-2">
                <Label>Produtividade esperada (sacas/ha)</Label>
                <Input
                  type="number"
                  value={formData.produtividadeEsperada}
                  onChange={(e) => setFormData({...formData, produtividadeEsperada: parseFloat(e.target.value) || 0})}
                />
              </div>

              <div className="space-y-2">
                <Label>Preço de venda (R$/saca)</Label>
                <Input
                  type="number"
                  value={formData.precoVenda}
                  onChange={(e) => setFormData({...formData, precoVenda: parseFloat(e.target.value) || 0})}
                />
              </div>

              <div className="space-y-3 pt-4 border-t border-stone-200">
                <Label className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Análise de Sensibilidade
                </Label>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Variação de Insumos</span>
                    <span className={formData.variacaoInsumos > 0 ? 'text-red-600' : 'text-green-600'}>
                      {formData.variacaoInsumos > 0 ? '+' : ''}{formData.variacaoInsumos}%
                    </span>
                  </div>
                  <Slider
                    value={[formData.variacaoInsumos]}
                    onValueChange={([v]) => setFormData({...formData, variacaoInsumos: v})}
                    min={-20}
                    max={30}
                    step={5}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Variação de Preço</span>
                    <span className={formData.variacaoPreco > 0 ? 'text-green-600' : 'text-red-600'}>
                      {formData.variacaoPreco > 0 ? '+' : ''}{formData.variacaoPreco}%
                    </span>
                  </div>
                  <Slider
                    value={[formData.variacaoPreco]}
                    onValueChange={([v]) => setFormData({...formData, variacaoPreco: v})}
                    min={-30}
                    max={30}
                    step={5}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Risco Climático</span>
                    <span className="text-amber-600">{formData.riscoCLimatico}%</span>
                  </div>
                  <Slider
                    value={[formData.riscoCLimatico]}
                    onValueChange={([v]) => setFormData({...formData, riscoCLimatico: v})}
                    min={0}
                    max={40}
                    step={5}
                  />
                </div>
              </div>

              <Button 
                onClick={calcularSimulacao}
                className="w-full bg-[#1B4332] hover:bg-[#2D6A4F] mt-4"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calcular Cenários
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Resultados */}
        <div className="lg:col-span-2 space-y-6">
          {!resultado ? (
            <Card className="bg-white border-stone-200">
              <CardContent className="p-12 text-center">
                <BarChart3 className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-stone-700">Configure sua simulação</h3>
                <p className="text-stone-500 mt-2">
                  Preencha os parâmetros ao lado e clique em "Calcular Cenários"
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Cenários */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Pessimista */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingDown className="w-5 h-5" />
                        <span className="font-bold">Pessimista</span>
                      </div>
                      <p className="text-3xl font-bold">{formatCurrency(resultado.pessimista.lucro)}</p>
                      <p className="text-red-200 text-sm mt-1">
                        Margem: {resultado.pessimista.margem.toFixed(1)}%
                      </p>
                      <div className="mt-4 pt-4 border-t border-red-400/30 text-sm text-red-100">
                        <p>Receita: {formatCurrency(resultado.pessimista.receitaTotal)}</p>
                        <p>Custo: {formatCurrency(resultado.pessimista.custoTotal)}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Realista */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Card className="bg-gradient-to-br from-[#2D6A4F] to-[#1B4332] text-white ring-4 ring-[#40916C]/30">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Target className="w-5 h-5" />
                        <span className="font-bold">Realista</span>
                        <Badge className="bg-white/20 text-white text-xs">Provável</Badge>
                      </div>
                      <p className="text-3xl font-bold">{formatCurrency(resultado.realista.lucro)}</p>
                      <p className="text-green-200 text-sm mt-1">
                        Margem: {resultado.realista.margem.toFixed(1)}%
                      </p>
                      <div className="mt-4 pt-4 border-t border-green-400/30 text-sm text-green-100">
                        <p>Receita: {formatCurrency(resultado.realista.receitaTotal)}</p>
                        <p>Custo: {formatCurrency(resultado.realista.custoTotal)}</p>
                        <p className="mt-2 font-medium">
                          Custo/saca: {formatCurrency(resultado.realista.custoPorSaca)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Otimista */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-5 h-5" />
                        <span className="font-bold">Otimista</span>
                      </div>
                      <p className="text-3xl font-bold">{formatCurrency(resultado.otimista.lucro)}</p>
                      <p className="text-green-200 text-sm mt-1">
                        Margem: {resultado.otimista.margem.toFixed(1)}%
                      </p>
                      <div className="mt-4 pt-4 border-t border-green-400/30 text-sm text-green-100">
                        <p>Receita: {formatCurrency(resultado.otimista.receitaTotal)}</p>
                        <p>Custo: {formatCurrency(resultado.otimista.custoTotal)}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>

              {/* Break-even e métricas */}
              <Card className="bg-white border-stone-200">
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="text-center">
                      <p className="text-sm text-stone-500">Produção Total</p>
                      <p className="text-2xl font-bold text-stone-800">
                        {resultado.realista.producaoTotal.toFixed(0)} sacas
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-stone-500">Custo por Saca</p>
                      <p className="text-2xl font-bold text-stone-800">
                        {formatCurrency(resultado.realista.custoPorSaca)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-stone-500">Break-even</p>
                      <p className="text-2xl font-bold text-amber-600">
                        {resultado.realista.breakEven.toFixed(1)} sc/ha
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-stone-500">Lucro por Hectare</p>
                      <p className="text-2xl font-bold text-[#40916C]">
                        {formatCurrency(resultado.realista.lucro / formData.area)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Botão IA */}
              <Button 
                onClick={gerarAnaliseIA}
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 py-6"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-5 h-5 mr-2" />
                )}
                Gerar Análise com Inteligência Artificial
              </Button>

              {/* Análise IA */}
              {analiseIA && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-purple-600" />
                        Análise Inteligente
                        <Badge className={
                          analiseIA.classificacao_risco === 'baixo' ? 'bg-green-100 text-green-700' :
                          analiseIA.classificacao_risco === 'medio' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }>
                          Risco {analiseIA.classificacao_risco}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div>
                        <p className="text-stone-700">{analiseIA.analise_geral}</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-bold text-red-700 mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            Riscos a Monitorar
                          </h4>
                          <ul className="space-y-2">
                            {analiseIA.riscos?.map((risco, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                                <span className="text-red-500 mt-1">•</span>
                                {risco}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-bold text-green-700 mb-3 flex items-center gap-2">
                            <Lightbulb className="w-4 h-4" />
                            Recomendações
                          </h4>
                          <ul className="space-y-2">
                            {analiseIA.recomendacoes?.map((rec, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                                <span className="text-green-500 mt-1">•</span>
                                {rec}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {analiseIA.ponto_atencao && (
                        <div className="p-4 bg-amber-100 rounded-xl">
                          <p className="text-sm text-amber-800">
                            <strong>⚠️ Ponto de Atenção:</strong> {analiseIA.ponto_atencao}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}